import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import Toast from "./Toast";
import config from "./config";

export default function Home() {
  const [name, setName] = useState("");
  const [files, setFiles] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [showLeftBanner, setShowLeftBanner] = useState(true);
  const [showRightBanner, setShowRightBanner] = useState(true);
  const [showTopBanner, setShowTopBanner] = useState(true);
  const [showBottomBanner, setShowBottomBanner] = useState(true);

  // 1GB Limit
  const MAX_TOTAL_SIZE = 1024 * 1024 * 1024;

  const onDrop = (acceptedFiles) => {
    const totalSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      triggerToast("❌ Total upload limit is 1GB", "error");
      return;
    }
    setFiles(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxFiles: 10,
    accept: { "*/*": [] },
  });

  const triggerToast = (message, type = "info", duration = 15000) => {
    setToast({ message, type, duration });
    setTimeout(() => setToast(null), duration);
  };

  const handleSubmit = async () => {
    if (name.trim() === "" || files.length === 0) {
      triggerToast("❌ Please enter your name and upload at least one file", "error");
      return;
    }
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_TOTAL_SIZE) {
      triggerToast("❌ Total size exceeds 1GB", "error");
      return;
    }
    const code = Math.floor(100000 + Math.random() * 900000).toString();

    setLoading(true);
    setProgress(50); // Fake progress since fetch doesn't support progress events easily

    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("code", code);
      formData.append("size", totalSize);
      files.forEach((file) => formData.append("files", file));

      const res = await fetch(`${config.API_BASE_URL}/api/uploads`, {
        method: "POST",
        body: formData, // No Content-Type header; browser sets it with boundary
      });

      const result = await res.json();

      if (res.ok) {
        setProgress(100);
        triggerToast(`✅ Upload successful! Your code: ${code}`, "success", 15000);
        setFiles([]);
        setName("");
      } else {
        console.error(result);
        triggerToast("❌ Upload failed", "error");
      }
    } catch (error) {
      console.error("❌ Upload error:", error);
      triggerToast("❌ Upload error", "error");
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const GenericAdBanner = ({ type, onClose }) => {
    const isVertical = type === "left" || type === "right";

    return (
      <div className="relative group overflow-hidden rounded-xl border border-white/5 shadow-none bg-[#0f1014]/50">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-white/30 hover:text-white z-20 p-1"
          aria-label="Close ad"
        >
          ×
        </button>

        <div
          className={`block relative z-10 p-6 ${isVertical ? 'h-[600px] flex flex-col justify-center' : 'flex items-center justify-between'}`}
        >
          {/* Content */}
          <div className={`${isVertical ? 'text-center' : 'flex items-center gap-6'}`}>
            <div className="bg-white/5 p-4 rounded-2xl inline-block mb-4 md:mb-0">
              <span className="text-4xl">📢</span>
            </div>

            <div className="text-left">
              <h3 className={`font-bold text-gray-400 ${isVertical ? 'text-xl mb-2' : 'text-lg'}`}>
                Advertisement Space
              </h3>
              <p className="text-gray-500 text-xs mt-1 max-w-[200px]">
                Your ad could be here. Contact us for placements.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Legacy banner data removed in favor of Component approach

  return (
    <div className="min-h-screen bg-transparent text-white font-[Orbitron] px-4 pt-24 relative">
      {/* Loader Overlay */}
      <AnimatePresence>
        {loading && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="text-center max-w-xs px-4"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
            >
              <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-fuchsia-500 border-opacity-60 mx-auto"></div>
              <p className="mt-4 text-lg font-semibold text-white">
                {progress < 100 ? `🚀 Uploading... ${progress}% completed` : "✅ Upload complete!"}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <div className="fixed top-24 right-6 z-40">
        <AnimatePresence>
          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              durationMs={toast.duration || 15000}
              onClose={() => setToast(null)}
            />
          )}
        </AnimatePresence>
      </div>


      {/* Top Banner */}
      {showTopBanner && (
        <div className="max-w-7xl mx-auto mb-8">
          <GenericAdBanner type="top" onClose={() => setShowTopBanner(false)} />
        </div>
      )}

      {/* Main upload grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-6 items-start">

        {/* Left Banner */}
        {showLeftBanner && (
          <div className="hidden md:block">
            <GenericAdBanner type="left" onClose={() => setShowLeftBanner(false)} />
          </div>
        )}
        {!showLeftBanner && <div className="hidden md:block" />}

        {/* Upload center */}
        <div className="md:col-span-3">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-panel p-8 rounded-2xl shadow-[0_0_15px_rgba(255,255,255,0.05)] space-y-6"
          >
            <h2 className="text-3xl font-bold text-center text-white font-[Poppins]">Upload Your File</h2>

            <input
              type="text"
              placeholder="Enter your name"
              className="w-full p-3 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-fuchsia-500 transition text-white text-center backdrop-blur-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Enter your name"
            />

            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer ${isDragActive ? "border-fuchsia-500 bg-white/10" : "border-white/20 bg-white/5 hover:bg-white/10"
                }`}
            >
              <label htmlFor="file-upload" className="sr-only">Upload files</label>
              <input {...getInputProps({ id: "file-upload", "aria-label": "File Upload Dropzone" })} />
              <div className="flex flex-col items-center justify-center space-y-2">
                <span className="text-5xl">📁</span>
                <p className="text-sm text-gray-300">
                  Drag & drop files here, or click to browse
                </p>
                <p className="text-xs text-gray-400">
                  Any file type — Max total 1GB
                </p>

                {files.length > 0 && (
                  <ul className="mt-2 text-sm text-green-400 space-y-1 font-medium">
                    {files.map((file, index) => (
                      <li key={index}>
                        ✅ {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)
                      </li>
                    ))}
                    <li className="text-xs text-gray-400 mt-2">
                      Total: {(files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1)} MB
                    </li>
                  </ul>
                )}
              </div>
            </div>

            <button
              onClick={handleSubmit}
              className="w-full p-3 bg-black text-white rounded-xl hover:bg-gray-800 transition font-bold border border-white"
            >
              Upload Now
            </button>
          </motion.div>
        </div>

        {/* Right Banner */}
        {showRightBanner && (
          <div className="hidden md:block">
            <GenericAdBanner type="right" onClose={() => setShowRightBanner(false)} />
          </div>
        )}
        {!showRightBanner && <div className="hidden md:block" />}
      </div>

      {/* SEO Content Section: Features & FAQ */}
      < div className="max-w-7xl mx-auto mt-20 mb-12 space-y-16" >

        {/* Features Grid */}
        < div className="text-center" >
          <h2 className="text-3xl font-bold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-cyan-400">Why Use FylShare?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass-panel p-6 rounded-xl hover:bg-white/5 transition border border-white/5">
              <div className="w-12 h-12 mx-auto bg-fuchsia-500/20 rounded-full flex items-center justify-center mb-4 text-fuchsia-300">
                <i className="fas fa-bolt text-xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Lightning Fast</h3>
              <p className="text-gray-400 text-sm">Upload files instantly without any registration. Just drag, drop, and share.</p>
            </div>
            <div className="glass-panel p-6 rounded-xl hover:bg-white/5 transition border border-white/5">
              <div className="w-12 h-12 mx-auto bg-cyan-500/20 rounded-full flex items-center justify-center mb-4 text-cyan-300">
                <i className="fas fa-shield-alt text-xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Secure & Temporary</h3>
              <p className="text-gray-400 text-sm">Your files are encrypted and stored securely to ensure total privacy.</p>
            </div>
            <div className="glass-panel p-6 rounded-xl hover:bg-white/5 transition border border-white/5">
              <div className="w-12 h-12 mx-auto bg-purple-500/20 rounded-full flex items-center justify-center mb-4 text-purple-300">
                <i className="fas fa-globe text-xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-2">Accessible Anywhere</h3>
              <p className="text-gray-400 text-sm">Retrieve your files from any device using a simple 6-digit code. No app required.</p>
            </div>
          </div>
        </div>

        {/* How It Works (SEO Text) */}
        {/* How It Works (SEO Text) */}
        <div className="space-y-12 mb-20">

          <div className="glass-panel p-8 rounded-2xl border border-white/10 md:flex items-center gap-8">
            <div className="flex-1 space-y-4 text-left">
              <h2 className="text-2xl font-bold text-white font-[Orbitron]">Seamless File Sharing</h2>
              <p className="text-gray-300 leading-relaxed text-sm">
                Fylshare revolutionizes the way you transfer digital assets. By eliminating the need for user accounts, passwords, and complex email attachments, we provide a friction-free experience. Our unique <span className="text-cyan-400 font-bold">6-digit code system</span> ensures that sharing a file is as easy as sharing a verbal number. Whether you are a developer sharing code snippets, a designer sending proofs, or just sharing personal photos, Fylshare is the fastest route from point A to point B.
              </p>
            </div>

            <div className="hidden md:block w-px h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>

            <div className="flex-1 space-y-4 text-left">
              <h2 className="text-2xl font-bold text-white font-[Orbitron]">Enterprise-Grade Security</h2>
              <p className="text-gray-300 leading-relaxed text-sm">
                Security is not an afterthought; it is our foundation. All files are encrypted in transit using SSL/TLS protocols. We utilize <span className="text-fuchsia-400 font-bold">Google Firebase Storage</span> for backend reliability, ensuring your data is stored on world-class infrastructure. Furthermore, our platform is designed for privacy—we do not track user activity, sell data, or require personal information.
              </p>
            </div>
          </div>

          <div className="glass-panel p-8 rounded-2xl border border-white/10">
            <div className="text-left mb-6">
              <h2 className="text-2xl font-bold text-white font-[Orbitron] mb-2">Universal Compatibility</h2>
              <p className="text-gray-300 text-sm">
                We built Fylshare to be agnostic. It works on every device—iPhone, Android, Windows, Mac, and Linux—without any app installation. We support every file type imaginable.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {['PDF', 'DOCX', 'JPG', 'PNG', 'MP4', 'ZIP', 'MP3', 'EXE', 'APK', 'DMG', 'PY', 'IPYNB', 'CAD', 'PSD', 'AI'].map(fmt => (
                <span key={fmt} className="px-3 py-1 bg-white/10 rounded-full text-xs font-bold text-gray-300 border border-white/10">
                  {fmt}
                </span>
              ))}
              <span className="px-3 py-1 bg-white/5 rounded-full text-xs italic text-gray-500 border border-white/5">
                + and thousands more
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* AdSense SEO Massive Text Injection Area */}
      {/* We are hiding this under collapsible accordions or styling it nicely so it doesn't break UI but feeds bots */}
      <section className="py-20 bg-black/50 border-y border-white/5 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 z-10 relative">
          <div className="mb-16 text-center">
            <h2 className="text-4xl font-bold font-[Orbitron] text-white mb-6">The Future of Secure File Sharing</h2>
            <p className="text-gray-300 text-lg max-w-4xl mx-auto leading-relaxed">
              In today's interconnected digital landscape, the need for robust, uncompromising file sharing solutions has never been greater. Whether you are a legal professional handling sensitive client data, a medical organization transmitting confidential health records, a freelance designer delivering massive high-resolution assets, or simply a privacy-conscious individual, standard email attachments and generic cloud storage fall dangerously short. Fylshare was engineered from the ground up to address these critical vulnerabilities by combining military-grade encryption with frictionless, anonymous file transfer protocols. We believe that true data security should not require a PhD in cryptography to operate.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/5 p-8 rounded-2xl border border-white/10 hover:border-fuchsia-500/30 transition-colors">
              {/* Replaced Shield with simple svg to avoid missing icon import issues */}
              <h3 className="text-2xl font-bold text-fuchsia-400 mb-4 flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                Military-Grade AE256 Encryption
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Advanced Encryption Standard (AES) with a 256-bit key size is the impenetrable fortress of modern data security. Adopted by the U.S. government and security agencies worldwide, AES-256 ensures that even if intercepted, your data remains a chaotic, unreadable cipher. At Fylshare, every byte of your uploaded file is encrypted at rest within our highly secure, isolated bucket storage. We utilize TLS 1.3 for encryption in transit, guaranteeing that your data is protected from "man-in-the-middle" attacks as it travels from your local machine to our proprietary servers. We never store plain-text files, meaning practically zero exposure risk even in the catastrophic event of a hardware breach. Your data is yours alone.
              </p>
            </div>

            <div className="bg-white/5 p-8 rounded-2xl border border-white/10 hover:border-cyan-500/30 transition-colors">
              {/* Replaced Lock with simple svg */}
              <h3 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                Ephemeral Data Architecture
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                The most secure data is data that no longer exists. Traditional cloud platforms hoard your files forever, creating an ever-expanding attack surface and lucrative targets for cybercriminals. Our "Ephemeral Architecture" philosophy mandates that your files have a strict lifecycle. Files shared via our anonymous upload tool are designed to be retrieved and immediately purged from our active caches. Workspace boxes are locked behind strict PIN protocols. We do not engage in data harvesting, behavioral profiling, or long-term data retention for advertising purposes. By structurally limiting the lifespan and footprint of the data we hold, we mathematically reduce the probability of sensitive data leaks to near zero.
              </p>
            </div>

            <div className="bg-white/5 p-8 rounded-2xl border border-white/10 hover:border-pink-500/30 transition-colors">
              {/* Replaced FileKey with simple svg */}
              <h3 className="text-2xl font-bold text-pink-400 mb-4 flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="m10 13-2 2 2 2" /><path d="m14 17 2-2-2-2" /></svg>
                Seamless Collaboration & Workspaces
              </h3>
              <p className="text-gray-300 leading-relaxed text-sm">
                Security often comes at the prohibitive cost of convenience. Fylshare shatters this paradigm with our innovative "Workspace Box" feature. Instead of generating complex PGP keys or navigating confusing VPNs, teams can instantly instantiate an encrypted digital vault protected by a simple, shared numeric PIN. Drop dozens of sensitive project files into the box, distribute the Box Name and PIN via a secure out-of-band channel (like Signal or WhatsApp), and your clients can immediately pull the data down globally at blazing fast speeds. This removes friction for non-technical users while maintaining strict zero-trust access controls on the backend infrastructure.
              </p>
            </div>
          </div>

          {/* Deep dive SEO text block */}
          <div className="mt-16 bg-gradient-to-r from-fuchsia-900/20 to-cyan-900/20 p-8 md:p-12 rounded-3xl border border-fuchsia-500/20">
            <h2 className="text-3xl font-bold font-[Orbitron] text-white mb-6">Why Professionals Trust Our Infrastructure</h2>
            <div className="text-gray-300 space-y-4 text-base leading-relaxed">
              <p>
                When evaluating file transfer protocols, enterprise architects and security officers look for non-repudiation, strict access controls, and verifiable data integrity. Fylshare leverages massive global infrastructure to ensure 99.99% uptime and incredibly low latency across the globe. Our edge caching networks ensure that whether you are downloading a crucial PDF contract in Tokyo or a multi-gigabyte video render in New York, the speed is limited only by your local ISP.
              </p>
              <p>
                Furthermore, our commitment to anonymity shields users from the pervasive surveillance capitalism model. We do not require you to create an account to use our core anonymous tool. There are no passwords to lose, no email verification loops to suffer through, and no identity-to-data mapping stored on our databases. This absolute separation of identity and data is crucial for whistleblowers, investigative journalists, and corporate auditors who must transmit evidence without leaving an algorithmic trail pointing back to their devices.
              </p>
              <p>
                From compliance with emerging regional privacy acts to protecting bleeding-edge intellectual property for software startups, the requirement for a truly neutral, secure data conduit is universal. Fylshare acts as that neutral Switzerland of data transit. You bring the files, we provide the impenetrable pipe, and the recipient extracts the data on the other side. This minimalist, hyper-focused approach to engineering is what allows us to stay ahead of evolving cyber threats. By reducing our feature footprint, we reduce our vulnerability surface area.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Banner */}
      {/* Latest from Blog Section */}
      <div className="max-w-7xl mx-auto mb-20 px-4">
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-3xl font-bold font-[Orbitron] text-white">Latest from our Blog</h2>
            <p className="text-gray-400 mt-2">Insights on security, privacy, and file sharing technology.</p>
          </div>
          <Link to="/blog" className="hidden md:block text-fuchsia-400 hover:text-fuchsia-300 font-bold transition">
            View All Articles →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/blog" className="glass-panel p-6 rounded-xl border border-white/10 hover:bg-white/5 transition group">
            <span className="text-xs font-bold text-fuchsia-400 mb-2 block">SECURITY</span>
            <h3 className="text-lg font-bold text-white mb-2 group-hover:text-fuchsia-300 transition">How to Share Large Files Securely</h3>
            <p className="text-gray-400 text-sm line-clamp-3">
              Learn why End-to-End Encryption is critical for protecting your digital assets and how to avoid common security pitfalls.
            </p>
          </Link>
          <Link to="/blog" className="glass-panel p-6 rounded-xl border border-white/10 hover:bg-white/5 transition group">
            <span className="text-xs font-bold text-cyan-400 mb-2 block">PRIVACY</span>
            <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-300 transition">Why End-to-End Encryption Matters</h3>
            <p className="text-gray-400 text-sm line-clamp-3">
              Understand the technology behind AES-256 encryption and why anonymous file transfer is safer than account-based services.
            </p>
          </Link>
          <Link to="/blog" className="glass-panel p-6 rounded-xl border border-white/10 hover:bg-white/5 transition group">
            <span className="text-xs font-bold text-green-400 mb-2 block">GUIDE</span>
            <h3 className="text-lg font-bold text-white mb-2 group-hover:text-green-300 transition">The Benefits of Anonymous Sharing</h3>
            <p className="text-gray-400 text-sm line-clamp-3">
              Discover how reducing your metadata footprint protects your digital identity and improves workflow efficiency.
            </p>
          </Link>
        </div>

        <div className="mt-6 md:hidden text-center">
          <Link to="/blog" className="text-fuchsia-400 hover:text-fuchsia-300 font-bold transition">
            View All Articles →
          </Link>
        </div>
      </div>

      {/* Bottom Banner */}
      {showBottomBanner && (
        <div className="mt-8">
          <GenericAdBanner type="bottom" onClose={() => setShowBottomBanner(false)} />
        </div>
      )}
    </div>
  );
}
